import { Link } from 'react-router-dom'
import PrimaryButton from '../../../../components/buttons/PrimaryButton'
import CheckboxField from '../../../../components/form/CheckboxField'
import TextInputField from '../../../../components/form/TextInputField'
import { LOGIN_COPY } from '../constants/loginContent'

function ErrorBanner({ message }) {
  if (!message) {
    return null
  }

  return (
    <div className="rounded-lg bg-error/10 px-4 py-3 text-sm text-error" role="alert">
      {message}
    </div>
  )
}

function SuccessBanner({ message }) {
  if (!message) {
    return null
  }

  return (
    <div className="rounded-lg bg-primary/10 px-4 py-3 text-sm text-primary" role="status">
      {message}
    </div>
  )
}

export default function LoginAuthForm({
  tenantUsername,
  identifier,
  password,
  rememberDevice,
  isSubmitting,
  error,
  successMessage,
  onTenantUsernameChange,
  onIdentifierChange,
  onPasswordChange,
  onRememberDeviceChange,
  onLoginSubmit,
  onForgotPasswordClick,
}) {
  return (
    <div className="my-auto w-full max-w-md">
      <div className="mb-12">
        <h1 className="font-headline text-5xl font-extrabold leading-none tracking-tight text-primary">
          Welcome <br />
          Back.
        </h1>
        <p className="mt-4 max-w-xs text-sm text-on-surface-variant">{LOGIN_COPY.subtitle}</p>
      </div>

      <form className="space-y-8" onSubmit={onLoginSubmit}>
        <ErrorBanner message={error} />
        <SuccessBanner message={successMessage} />

        {/* Tenant Username — identifies the organisation database */}
        <TextInputField
          id="tenantUsername"
          label={LOGIN_COPY.tenantUsernameLabel}
          value={tenantUsername}
          onChange={onTenantUsernameChange}
          placeholder={LOGIN_COPY.tenantUsernamePlaceholder}
          icon="domain"
          autoComplete="organization"
          disabled={isSubmitting}
        />

        <TextInputField
          id="identifier"
          label={LOGIN_COPY.identifierLabel}
          value={identifier}
          onChange={onIdentifierChange}
          placeholder={LOGIN_COPY.identifierPlaceholder}
          icon="alternate_email"
          autoComplete="username"
          disabled={isSubmitting}
        />

        <TextInputField
          id="password"
          label={LOGIN_COPY.passwordLabel}
          value={password}
          onChange={onPasswordChange}
          placeholder={LOGIN_COPY.passwordPlaceholder}
          icon="lock"
          type="password"
          autoComplete="current-password"
          disabled={isSubmitting}
          trailingAction={
            <button
              type="button"
              onClick={onForgotPasswordClick}
              disabled={isSubmitting}
              className="text-xs font-bold text-primary hover:underline disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60"
            >
              {LOGIN_COPY.forgotPassword}
            </button>
          }
        />

        <CheckboxField
          id="rememberDevice"
          label={LOGIN_COPY.rememberDevice}
          checked={rememberDevice}
          onChange={onRememberDeviceChange}
          disabled={isSubmitting}
        />

        <PrimaryButton type="submit" icon="arrow_forward" loading={isSubmitting} disabled={isSubmitting}>
          {LOGIN_COPY.loginCta}
        </PrimaryButton>
      </form>

      <div className="mt-8 border-t border-surface-variant/30 pt-6 text-center space-y-3">
        <p className="text-xs text-on-surface-variant">
          Are you an employee?{' '}
          <Link
            to="/self-service/login"
            className="font-bold text-primary hover:underline inline-flex items-center gap-1"
          >
            <span className="material-symbols-outlined text-sm">badge</span>
            <span>Employee Self-Service Portal</span>
          </Link>
        </p>
        <p className="text-xs text-on-surface-variant">
          {LOGIN_COPY.requestAccessLabel}{' '}
          <a
            href={`${import.meta.env.VITE_WEBSITE_URL || 'http://localhost:3000'}#pricing`}
            className="font-bold text-primary hover:underline"
          >
            {LOGIN_COPY.requestAccessLink}
          </a>
        </p>
      </div>
    </div>
  )
}
